import * as React from 'react'

import type { FragmentOf } from '~publish/graphql'

import type { EngagementReplySuggestions_SavedReply } from '../../components/EngagementReplySuggestions/EngagementReplySuggestions'
import { findMostSimilarSavedReply } from '../../util/savedRepliesHelpers'

import { useSavedReplies } from './useSavedReplies'

type SimilaritySavedReply = FragmentOf<
  typeof EngagementReplySuggestions_SavedReply
>

/**
 * Resolves the saved reply most similar to a given text (comment or mention
 * body) using cosine similarity. Wraps `useSavedReplies` + `findMostSimilarSavedReply`
 * and memoizes the result so it only recomputes when the text or the saved
 * replies change.
 *
 * @param text - The engagement text to match saved replies against.
 * @returns The most similar saved reply, or `null` when there's no text, no
 * saved replies, or nothing clears the similarity threshold.
 */
export function useMostSimilarSavedReply(
  text: string | null | undefined,
): SimilaritySavedReply | null {
  const { savedReplies } = useSavedReplies()

  return React.useMemo<SimilaritySavedReply | null>(() => {
    if (!text || !savedReplies || !savedReplies.length) {
      return null
    }

    try {
      return findMostSimilarSavedReply({
        commentText: text,
        savedReplies,
      })
    } catch (error) {
      // Local diagnostic for a non-blocking transient failure in the
      // similarity helper; intentionally not routed to Sentry to avoid noise.
      // eslint-disable-next-line no-console
      console.error('Error finding similar saved replies:', error)
      return null
    }
  }, [text, savedReplies])
}
